import { Injectable } from '@angular/core';

import type { GetScrollableNodeCleanupsByTypeRequest } from '@reach/interfaces/node-cleanup/node-cleanup.requests';
import type {
	CleanupClusterResponse,
	DetectNodeCleanupResponse,
	DiscardNodeCleanupResponse,
	GetPaginatedNodeCleanupsByTypeResponse,
	MergeClusterResponse,
	UpdateNodeCleanupResponse,
} from '@reach/interfaces/node-cleanup/node-cleanup.responses';
import {
	DEFAULT_NODE_CLEANUP_PAGE_SIZE,
	type NodeCleanupInterface,
	type UpdateNodeCleanup,
} from '@reach/interfaces/node-cleanup/node-cleanup.types';
import {
	OrderByEnum,
	type PaginationCursor,
} from '@reach/interfaces/requests/request-pagination.interface';

import { BaseApiService } from '~app-client/api/_internal';

@Injectable({ providedIn: 'root' })
export class NodeCleanupApiService extends BaseApiService {
	protected commonPath = ['node-cleanups'];

	public async detectNodeCleanup(): Promise<DetectNodeCleanupResponse> {
		return await this.http.post<DetectNodeCleanupResponse>(this.url('scan'), {});
	}

	public async getPaginatedNodeCleanupsByType(
		query: GetScrollableNodeCleanupsByTypeRequest
	): Promise<GetPaginatedNodeCleanupsByTypeResponse> {
		return await this.http.get<GetPaginatedNodeCleanupsByTypeResponse>(
			this.url(),
			this.toPaginationParams(query)
		);
	}

	public async updateNodeCleanup(
		nodeCleanupId: string,
		body: UpdateNodeCleanup
	): Promise<UpdateNodeCleanupResponse> {
		return await this.http.patch<UpdateNodeCleanupResponse>(this.url(nodeCleanupId), body);
	}

	public async mergeCluster(nodeCleanupId: string): Promise<MergeClusterResponse> {
		return await this.http.post<MergeClusterResponse>(this.url(nodeCleanupId, 'merge'), {});
	}

	public async cleanupCluster(
		nodeCleanupId: string,
		nodeIds: readonly string[] = []
	): Promise<CleanupClusterResponse> {
		return await this.http.post<CleanupClusterResponse>(this.url(nodeCleanupId, 'cleanup'), {
			nodeIds,
		});
	}

	public async deleteNodeCleanup(nodeCleanupId: string): Promise<DiscardNodeCleanupResponse> {
		return await this.http.delete<DiscardNodeCleanupResponse>(this.url(nodeCleanupId));
	}

	private toPaginationParams(
		query: GetScrollableNodeCleanupsByTypeRequest
	): Record<string, string> {
		const params: Record<string, string> = {
			type: query.type,
			page: String(query.page ?? 0),
			size: String(query.size ?? DEFAULT_NODE_CLEANUP_PAGE_SIZE),
			orderBy: query.orderBy ?? OrderByEnum.DESC,
		};
		if (query.sortBy !== undefined) {
			params.sortBy = query.sortBy;
		}
		if (query.cursor !== undefined) {
			params.cursor = this.paginationCursorToParam(query.cursor);
		}
		return params;
	}

	private paginationCursorToParam(
		cursor: PaginationCursor<NodeCleanupInterface, 'id'>
	): string {
		// Keyset cursors are JSON-encoded; scalar cursors are stringified.
		return typeof cursor === 'object' ? JSON.stringify(cursor) : String(cursor);
	}
}
